import { StaticImage } from "gatsby-plugin-image";
import styled from "styled-components";
import { VinoDataOffer } from "../../types/vino-data-types";
import useAuth from "../../hooks/use-auth";
import { getExternalUserID } from "../../../api/src/utils/auth";
import { trackEvent } from "../../utils/track-event";
import { useEffect } from "react";
import { isProduction } from "../../utils/environment-helpers";
import RelatedProductItems from "../related-offers/RelatedProductItems";
import { navigate } from "gatsby-link";
import useScreenSize from "../../hooks/use-screen-size";
import LoginForm from "../login-form/LoginForm";
import SignUpForm from "../sign-up-form/SignupForm";
import React from "react";
import { WhiskymofoBlackIcon } from "./Icons";
import { isWhiskymofo } from "../../utils/is-page";
import { RelatedDefaultItems } from "../related-offers/RelatedDefaultItems";

const ModalOverlay = styled.div`
  ${(props) => (props.theme === "modal" ? "padding: 10px;" : "padding:0px;")}
  font-family: Graphik;
  position: relative;
  display: flex;
  background-color: ${(props) =>
    props.theme === "modal" ? "rgba(2, 2, 2, 0.67)" : "rgba(19, 19, 20, 1)"};
  justify-content: center;
  align-items: center;
  z-index: 1;
  overflow: hidden;
`;

const ModalBackground = styled.div`
  position: relative;
  ${(props) =>
    props.theme === "modal" ? "padding: 50px 40px 50px 40px;" : "padding: 0px"}
  background-color: ${(props) =>
    props.theme === "modal" ? "rgba(255, 255, 255, 0)" : "rgba(19, 19, 20, 1)"};
  text-align: start;
  width: 100%;
  max-width: 1160px;
  @media (max-width: 768px) {
    ${(props) =>
      props.theme === "modal"
        ? "padding: 20px 0px 20px 0px;"
        : "padding: 50px 10px;"}
  }
`;

const ModalContainer = styled.div`
  position: relative;
  display: flex;
`;

const ModalFloatingButton = styled.div<{ isSpirit?: boolean }>`
  z-index: 1;
  position: absolute;
  top: ${({ isSpirit }) => (isSpirit ? "auto" : "15px")};
  bottom: ${({ isSpirit }) => (isSpirit ? "22px" : "auto")};
  right: 10px;
  align-items: right;
  background-color: white;
  color: black;
  font-weight: 700;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  font-size: 0.7em;
  transition: background-color 0.3s;
  display: inline-block;
  margin: 0 10px;
  padding: 10px 30px;
`;

const BackButton = styled.div<{ isSpirit?: boolean }>`
  background-color: white;
  color: black;
  font-weight: 700;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  font-size: 0.7em;
  transition: background-color 0.3s;
  display: inline-block;
  margin: 0 10px;
  padding: 10px 30px;
`;

const CheckStatus = styled.div`
  z-index: 1;
  position: absolute;
  bottom: 25px;
  color: black;
  font-weight: 700;
  font-size: 0.9em;
  width: 100%;
  text-align: center;
  padding: 0 15px;
`;

const FullLeft = styled.div`
  width: 580px;
  background-color: rgba(19, 19, 20, 1);
  color: white;
  display: flex;
  justify-content: center;
  flex-direction: column;
  padding: 40px 36px;
  @media (max-width: 768px) {
    padding: 12px 18px;
    border-top-right-radius: 15px;
    border-bottom-right-radius: 15px;
  }
`;

const FullRight = styled.div`
  width: 580px;
  @media (max-width: 768px) {
    display: none;
  }

  div {
    height: 100%;
  }
`;

const ModalLeft = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 600px;
  height: 600px;
  background-color: white;
  color: black;
  border-top-left-radius: 15px;
  border-bottom-left-radius: 15px;
  padding: 40px 36px;
  @media (max-width: 768px) {
    padding: 36px 18px;
    border-top-right-radius: 5px;
    border-bottom-right-radius: 5px;
    border-top-left-radius: 5px;
    border-bottom-left-radius: 5px;
    height: 100%;
  }
`;

const ModalRight = styled.div`
  position: relative;
  width: 600px;
  height: 600px;
  @media (max-width: 768px) {
    display: none;
  }

  .gatsby-image-wrapper {
    width: 100%;
    height: 100%;
    border-top-right-radius: 15px;
    border-bottom-right-radius: 15px;
  }
`;

const ModalTopLine = styled.div<{ isSpirit?: boolean }>`
  margin-top: ${({ isSpirit }) => (isSpirit ? "35px" : "70px")};
  margin-bottom: 20px;
  font-weight: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 333px;
  @media (max-width: 768px) {
    font-size: 16px;
    margin-top: 0px;
  }
`;

const IconContainer = styled.div`
  margin-right: 10px;
`;

const ModalBottomLine = styled.div`
  margin: 40px;
  font-family: Graphik;
  font-size: 20px;
  font-weight: 400;
  text-align: center;
  width: 100%;
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const ModalTitle = styled.h2`
  text-align: center;
  margin-bottom: 20px;
  margin-top: 10px;
  font-size: 30px;
  font-weight: 100;
  white-space: pre-line;
  max-width: 400px;
  width: 100%;
  @media (max-width: 768px) {
    font-size: 36px;
    margin-bottom: 30px;
  }
`;

const ModalSuggestions = styled.div`
  margin-top: 10px;
`;

type CauseType = "guest_cannot_see" | "tier_too_low" | "email_not_valid";

const PayWall = React.memo(
  ({
    title,
    dealTitle,
    LoginButton,
    offer,
    cause,
    theme,
    checkStatus,
    category,
    page_tier,
    user_tier,
    userEmail,
    isSignUp,
    isSpirit_,
  }: {
    title?: string | JSX.Element;
    dealTitle?: string;
    isSignUp?: boolean;
    LoginButton?: boolean;
    isSpirit_?: boolean;
    offer?: VinoDataOffer;
    cause: CauseType;
    theme?: "modal" | "full";
    checkStatus?: boolean;
    category?: "Offer" | "Event" | "Spirit";
    page_tier?: string;
    user_tier?: string;
    userEmail?: string | null | undefined;
  }) => {
    const { user } = useAuth();
    const userId = getExternalUserID(user);
    const { isMobile } = useScreenSize();
    const isSpirit = isSpirit_ || category === "Spirit" || false;

    const renderModalTopLine = () => {
      switch (category) {
        case "Offer":
          return !isWhiskymofo ? (
            <ModalTopLine>
              <IconContainer>
                <StaticImage
                  width={18}
                  quality={100}
                  formats={["auto", "webp", "avif"]}
                  src="https://res.cloudinary.com/vinomofo/image/upload/v1738106222/paywall/lock.png"
                  alt="Lock icon"
                />
              </IconContainer>
              THIS IS A VIM EXCLUSIVE OFFER
            </ModalTopLine>
          ) : (
            <ModalTopLine isSpirit={true}>
              <WhiskymofoBlackIcon width="248" height="75" />
            </ModalTopLine>
          );
        case "Event":
          return (
            <ModalTopLine>
              <IconContainer>
                <StaticImage
                  width={18}
                  quality={100}
                  formats={["auto", "webp", "avif"]}
                  src="https://res.cloudinary.com/vinomofo/image/upload/f_auto,q_auto,w_18/v1738106222/paywall/lock.png"
                  alt="Lock icon"
                />
              </IconContainer>
              THIS IS A VIM EXCLUSIVE EVENT
            </ModalTopLine>
          );
        case "Spirit":
          return (
            <ModalTopLine isSpirit={true}>
              <WhiskymofoBlackIcon width="248" height="75" />
            </ModalTopLine>
          );
        default:
          return null;
      }
    };

    const renderModalRightImage = () => {
      switch (category) {
        case "Offer":
          return !isWhiskymofo ? (
            <StaticImage
              width={600}
              height={600}
              quality={100}
              formats={["auto", "webp", "avif"]}
              objectFit="cover"
              src="https://res.cloudinary.com/vinomofo/image/upload/f_auto,q_auto/v1739174466/paywall/paywall-modal-side.png"
              alt="Vinomofo Paywall"
            />
          ) : (
            <StaticImage
              width={600}
              height={600}
              quality={100}
              formats={["auto", "webp", "avif"]}
              objectFit="cover"
              src="https://res.cloudinary.com/vinomofo/image/upload/f_auto,q_auto/v1757046172/paywall/whiskymofo-paywall-v2.jpg"
              alt="Whiskymofo Paywall"
            />
          );
        case "Event":
          return (
            <StaticImage
              width={600}
              height={600}
              quality={100}
              formats={["auto", "webp", "avif"]}
              objectFit="cover"
              src="https://res.cloudinary.com/vinomofo/image/upload/f_auto,q_auto/v1739426285/paywall/SORRY_TRON_updated_2.png"
              alt="Event Paywall"
            />
          );
        case "Spirit":
          return (
            <StaticImage
              width={600}
              height={600}
              quality={100}
              formats={["auto", "webp", "avif"]}
              objectFit="cover"
              src="https://res.cloudinary.com/vinomofo/image/upload/f_auto,q_auto/v1757046172/paywall/whiskymofo-paywall-v2.jpg"
              alt="Spirit Paywall"
            />
          );
        default:
          return null;
      }
    };

    useEffect(() => {
      trackEvent("Paywall Shown", {
        category: category,
        label: cause,
        page_tier: page_tier,
        user_tier: user_tier,
      });
    }, []);

    return (
      <>
        <ModalOverlay theme={theme}>
          <ModalBackground theme={theme}>
            <ModalContainer>
              {theme === "modal" && !isMobile && (
                <ModalFloatingButton
                  isSpirit={isSpirit}
                  onClick={() =>
                    isSpirit ? window.history.back() : navigate("/wines/")
                  }
                >
                  BACK
                </ModalFloatingButton>
              )}
              {theme === "modal" && (
                <ModalLeft>
                  {renderModalTopLine()}
                  <ModalTitle>{title}</ModalTitle>
                  {LoginButton && (
                    <div
                      style={{
                        alignItems: "center",
                        maxWidth: "333px",
                        width: "100%",
                      }}
                    >
                      <LoginForm
                        isSpirit={isSpirit}
                        formLocation="Paywall"
                        guestEmail={userEmail}
                      />
                    </div>
                  )}
                  {isSignUp && (
                    <div
                      style={{
                        alignItems: "center",
                        maxWidth: "333px",
                        width: "100%",
                      }}
                    >
                      <SignUpForm
                        isSpirit={isSpirit}
                        formLocation="Paywall"
                        userEmail={userEmail}
                        category={category}
                      />
                    </div>
                  )}
                  {checkStatus && (
                    <CheckStatus>
                      CHECK YOUR VIM STATUS{" "}
                      <a
                        href="/account/very-important-mofo"
                        style={{ color: "black" }}
                      >
                        HERE
                      </a>
                    </CheckStatus>
                  )}
                  <ModalSuggestions></ModalSuggestions>
                </ModalLeft>
              )}
              {theme === "full" && (
                <FullLeft>
                  {renderModalTopLine()}
                  <ModalTitle>{title}</ModalTitle>
                  {LoginButton && (
                    <div
                      style={{
                        alignItems: "center",
                        maxWidth: "333px",
                        width: "100%",
                      }}
                    >
                      <LoginForm
                        isSpirit={isSpirit}
                        formLocation="Paywall"
                        guestEmail={userEmail}
                      />
                    </div>
                  )}
                  {isSignUp && (
                    <div
                      style={{
                        alignItems: "center",
                        maxWidth: "333px",
                        width: "100%",
                      }}
                    >
                      <SignUpForm
                        isSpirit={isSpirit}
                        formLocation="Paywall"
                        userEmail={userEmail}
                        category={category}
                      />
                    </div>
                  )}
                  {checkStatus && (
                    <CheckStatus>
                      CHECK YOUR VIM STATUS{" "}
                      <a
                        href="/account/very-important-mofo"
                        style={{ color: "black" }}
                      >
                        HERE
                      </a>
                    </CheckStatus>
                  )}
                  <ModalSuggestions />
                </FullLeft>
              )}
              {theme === "modal" && (
                <ModalRight>
                  <>
                    {renderModalRightImage()}
                    {/* { theme === "modal" && !isMobile && <Box 
                      as="span" 
                      position="absolute" 
                      zIndex="1"
                      left="0"
                      width="100%"
                      top={isSpirit ? "auto" : "15px"}
                      bottom={isSpirit ? "22px" : "auto"}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      p="4px 27px"
                    >
                      {isSpirit && <WhiskymofoGoldIcon width="134" height="40" /> }
                      <BackButton onClick={() => navigate(category !== "Spirit" ? "/wines/" : "/spirits/")}>BACK</BackButton>
                    </Box>
                  } */}
                  </>
                </ModalRight>
              )}
              {theme === "full" && (
                <FullRight>{renderModalRightImage()}</FullRight>
              )}
            </ModalContainer>
          </ModalBackground>
        </ModalOverlay>

        {dealTitle && (
          <>
            <ModalContainer>
              <ModalBottomLine>{dealTitle}</ModalBottomLine>
            </ModalContainer>

            {isProduction ? (
              <RelatedProductItems
                indexName={process.env.GATSBY_ALGOLIA_OFFERS_INDEX}
                offerIds={[offer?.id]}
                heading=""
                subheading=""
                columns={4}
                limit={4}
                userId={userId}
                hidden={offer?.hidden}
              />
            ) : (
              <RelatedDefaultItems heading="You might also like…" limit={4} />
            )}
          </>
        )}
      </>
    );
  }
);

PayWall.displayName = "PayWall";

export default PayWall;
